import { useEffect, useState } from 'react';
import './App.css';
import PropertyRegistration from './PropertyRegistration';
import PropertyDetail from './PropertyDetail';
import PropertyListMap from './PropertyListMap';
import ExposureCard from './ExposureCard';
import ExposureMap from './ExposureMap';
import { API_BASE_URL, convertImageUrl } from './config';
import RoomManagement from './RoomManagement/RoomManagement';
import AdminTab from './AdminTab';
import MemberListTab from './MemberListTab';
import InquiryBoard from './InquiryBoard';
import UpdateBoard from './UpdateBoard';
import NoticeBoard from './NoticeBoard';
import TransferBoard from './TransferBoard';
import { useResponsive } from './hooks/useResponsive';

interface Property {
  id: number;
  status?: string;
  host_name?: string;
  host_phone?: string;
  manager?: string;
  space: {
    road_address: string;
    jibun_address?: string;
    floor: string;
    detail_address?: string;
    building_type: string;
    room_count: number;
    bathroom_count: number;
    kitchen_count: number;
    living_room_count: number;
    has_duplex: boolean;
    exclusive_area: number;
    has_elevator: boolean;
    parking_available: boolean;
    parking_option?: string;
    parking_info?: string;
  } | null;
  rent: {
    weekly_rent: number;
    deposit: number;
    weekly_maintenance_fee: number;
    maintenance_includes_electricity: boolean;
    maintenance_includes_water: boolean;
    maintenance_includes_gas: boolean;
    maintenance_detail?: string;
    checkout_cleaning_fee: number;
    cancellation_penalty: string;
    refund_policy_note: boolean;
  } | null;
  descriptions: {
    room_name: string;
    room_introduction: string;
    usage_guide?: string;
    transportation_info: string;
    additional_info?: string;
  } | null;
  photos: Array<{
    photo_url: string;
    display_order: number;
  }>;
  options: {
    basic_options: string[];
    additional_options: string[];
    pets_allowed: boolean;
  } | null;
  schedule: {
    min_contract_weeks: number;
    available_from?: string;
    available_until?: string;
  } | null;
  long_term_discounts: Array<{
    weeks: number;
    discount_rate: number;
  }>;
  immediate_discounts: Array<{
    days: number;
    discount_amount: number;
  }>;
}

interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: 'host' | 'admin';
}

function App() {
  const { isMobile, isTablet } = useResponsive();

  // Auth state (shared across all tabs)
  const [token, setToken] = useState<string>(localStorage.getItem('str_token') || '');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const [properties, setProperties] = useState<Property[]>([]);
  const [reviewProperties, setReviewProperties] = useState<Property[]>([]);
  const [completedProperties, setCompletedProperties] = useState<Property[]>([]);
  const [exposedProperties, setExposedProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [showRegistration, setShowRegistration] = useState<boolean>(false);
  const [editPropertyId, setEditPropertyId] = useState<number | undefined>(undefined);
  const [activeSection, setActiveSection] = useState<'rooms' | 'registration' | 'admin' | 'admin2' | 'review' | 'criteria' | 'completed' | 'exposure' | 'inquiry' | 'updates' | 'stats' | 'notices' | 'transfers'>('rooms');
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [aiReviewResult, setAiReviewResult] = useState<any>(null);
  const [showAiReviewModal, setShowAiReviewModal] = useState<boolean>(false);
  const [aiReviewing, setAiReviewing] = useState<boolean>(false);
  const [hoveredPropertyId, setHoveredPropertyId] = useState<number | null>(null);
  const [deleteMode, setDeleteMode] = useState<boolean>(false);
  const [selectedForDelete, setSelectedForDelete] = useState<Set<number>>(new Set());
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantMessage, setGrantMessage] = useState('');
  const [changePwCurrent, setChangePwCurrent] = useState('');
  const [changePwNew, setChangePwNew] = useState('');
  const [changePwConfirm, setChangePwConfirm] = useState('');
  const [changePwMessage, setChangePwMessage] = useState('');
  const [changePwError, setChangePwError] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPhone, setSignupPhone] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupPasswordConfirm, setSignupPasswordConfirm] = useState('');
  const [signupError, setSignupError] = useState('');
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStep, setForgotStep] = useState<'email' | 'code'>('email');
  const [forgotCode, setForgotCode] = useState('');
  const [forgotNewPw, setForgotNewPw] = useState('');
  const [forgotNewPwConfirm, setForgotNewPwConfirm] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [hasNewInquiry, setHasNewInquiry] = useState(false);
  const [hasNewUpdate, setHasNewUpdate] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  // Auth check on mount
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    fetch(`${API_BASE_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => setUser(data))
      .catch(() => { setToken(''); localStorage.removeItem('str_token'); });
  }, [token]);

  // 새 콘텐츠 뱃지: 7일 이내 항목 여부 확인
  useEffect(() => {
    if (!token) return;
    const sinceMs = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const h = { Authorization: `Bearer ${token}` };
    fetch(`${API_BASE_URL}/api/inquiries`, { headers: h })
      .then(r => r.ok ? r.json() : [])
      .then((data: any[]) => setHasNewInquiry(data.some((d: any) => new Date(d.created_at).getTime() > sinceMs)))
      .catch(() => {});
    fetch(`${API_BASE_URL}/api/updates`, { headers: h })
      .then(r => r.ok ? r.json() : [])
      .then((data: any[]) => setHasNewUpdate(data.some((d: any) => new Date(d.created_at).getTime() > sinceMs)))
      .catch(() => {});
  }, [token]);

  // 스크롤 페이드인 애니메이션
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
          observer.unobserve(e.target);
        }
      }),
      { threshold: 0.15 }
    );
    document.querySelectorAll('.fade-in-section').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [user]);

  const handleLogin = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: loginIdentifier, password: loginPassword }),
      });
      const data = await res.json();
      if (!res.ok) { setLoginError(data.detail || '로그인 실패'); return; }
      setToken(data.token);
      localStorage.setItem('str_token', data.token);
      setUser(data.user);
      setActiveSection('rooms');
      setLoginError('');
    } catch (e) { setLoginError('서버 연결 실패'); }
  };

  const handleLogout = () => {
    setToken('');
    setUser(null);
    setShowUserMenu(false);
    localStorage.removeItem('str_token');
  };

  const handleGrantAdmin = async () => {
    if (!grantEmail.trim()) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/grant-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ email: grantEmail }),
      });
      const data = await res.json();
      if (!res.ok) { setGrantMessage(`✗ ${data.detail}`); return; }
      setGrantMessage(`✓ ${data.message}`);
      setGrantEmail('');
    } catch { setGrantMessage('✗ 서버 연결 실패'); }
  };

  const handleChangePassword = async () => {
    setChangePwError('');
    setChangePwMessage('');
    if (!changePwCurrent || !changePwNew) { setChangePwError('모든 항목을 입력해주세요'); return; }
    if (changePwNew !== changePwConfirm) { setChangePwError('새 비밀번호가 일치하지 않습니다'); return; }
    if (changePwNew.length < 4) { setChangePwError('비밀번호는 4자 이상이어야 합니다'); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ current_password: changePwCurrent, new_password: changePwNew }),
      });
      const data = await res.json();
      if (!res.ok) { setChangePwError(data.detail); return; }
      setChangePwMessage('비밀번호가 변경되었습니다');
      setChangePwCurrent(''); setChangePwNew(''); setChangePwConfirm('');
    } catch { setChangePwError('서버 연결 실패'); }
  };

  useEffect(() => {
    const close = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest('[data-user-menu]')) setShowUserMenu(false);
    };
    if (showUserMenu) document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showUserMenu]);

  const handleSignup = async () => {
    if (!signupName.trim() || !signupEmail.trim() || !signupPhone.trim() || !signupPassword) {
      setSignupError('모든 필드를 입력해주세요');
      return;
    }
    if (signupPassword !== signupPasswordConfirm) {
      setSignupError('비밀번호가 일치하지 않습니다');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: signupEmail, password: signupPassword, name: signupName, phone: signupPhone }),
      });
      const data = await res.json();
      if (!res.ok) { setSignupError(data.detail || '가입 실패'); return; }
      setToken(data.token);
      localStorage.setItem('str_token', data.token);
      setUser(data.user);
      setActiveSection('rooms');
      setShowSignupModal(false);
    } catch { setSignupError('서버 연결 실패'); }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail.trim()) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      if (!res.ok) { const d = await res.json(); setForgotError(d.detail || '오류가 발생했습니다'); return; }
      setForgotStep('code');
      setForgotMessage('이메일로 인증 코드를 발송했습니다');
      setForgotError('');
    } catch { setForgotError('서버 연결 실패'); }
  };

  const handleResetPassword = async () => {
    if (!forgotCode.trim() || !forgotNewPw) { setForgotError('모든 항목을 입력해주세요'); return; }
    if (forgotNewPw !== forgotNewPwConfirm) { setForgotError('비밀번호가 일치하지 않습니다'); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail, code: forgotCode, new_password: forgotNewPw }),
      });
      const data = await res.json();
      if (!res.ok) { setForgotError(data.detail || '오류가 발생했습니다'); return; }
      setShowForgotModal(false);
      setShowLoginModal(true);
      setForgotEmail(''); setForgotCode(''); setForgotNewPw(''); setForgotNewPwConfirm('');
      setForgotStep('email'); setForgotMessage(''); setForgotError('');
    } catch { setForgotError('서버 연결 실패'); }
  };

  const fetchProperties = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/properties`, { headers: authHeaders })
      .then(response => response.json())
      .then(data => {
        setProperties(data.properties);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to fetch properties');
        setLoading(false);
      });
  };

  const fetchReviewProperties = () => {
    fetch(`${API_BASE_URL}/api/properties/review`, { headers: authHeaders })
      .then(response => response.json())
      .then(data => {
        setReviewProperties(data.properties);
      })
      .catch((err) => {
        console.error('Failed to fetch review properties:', err);
      });
  };

  const fetchCompletedProperties = () => {
    fetch(`${API_BASE_URL}/api/properties/completed`, { headers: authHeaders })
      .then(response => response.json())
      .then(data => {
        setCompletedProperties(data.properties);
      })
      .catch((err) => {
        console.error('Failed to fetch completed properties:', err);
      });
  };

  const fetchExposedProperties = () => {
    fetch(`${API_BASE_URL}/api/properties/exposed`, { headers: authHeaders })
      .then(response => response.json())
      .then(data => {
        setExposedProperties(data.properties);
      })
      .catch((err) => {
        console.error('Failed to fetch exposed properties:', err);
      });
  };

  const fetchPropertyDetail = (propertyId: number) => {
    fetch(`${API_BASE_URL}/api/properties/${propertyId}`, { headers: authHeaders })
      .then(response => response.json())
      .then(data => {
        // API 응답을 Property 인터페이스 형식으로 변환
        const detailProperty: Property = {
          id: data.id,
          status: data.status,
          space: {
            road_address: data.road_address,
            jibun_address: data.jibun_address,
            floor: data.floor,
            detail_address: data.detail_address,
            building_type: data.building_type,
            room_count: data.room_count,
            bathroom_count: data.bathroom_count,
            kitchen_count: data.kitchen_count,
            living_room_count: data.living_room_count,
            has_duplex: data.has_duplex,
            exclusive_area: data.exclusive_area,
            has_elevator: data.has_elevator,
            parking_available: data.parking_available,
            parking_option: data.parking_option,
            parking_info: data.parking_info
          },
          rent: {
            weekly_rent: data.weekly_rent,
            deposit: data.deposit,
            weekly_maintenance_fee: data.weekly_maintenance_fee,
            maintenance_includes_electricity: data.maintenance_includes_electricity,
            maintenance_includes_water: data.maintenance_includes_water,
            maintenance_includes_gas: data.maintenance_includes_gas,
            maintenance_detail: data.maintenance_detail,
            checkout_cleaning_fee: data.checkout_cleaning_fee,
            cancellation_penalty: data.cancellation_penalty,
            refund_policy_note: data.refund_policy_note
          },
          descriptions: {
            room_name: data.room_name,
            room_introduction: data.room_introduction,
            usage_guide: data.usage_guide,
            transportation_info: data.transportation_info,
            additional_info: data.additional_info
          },
          photos: data.photo_urls?.map((url: string, index: number) => ({
            photo_url: url,
            display_order: index + 1
          })) || [],
          options: {
            basic_options: data.basic_options || [],
            additional_options: data.additional_options || [],
            pets_allowed: data.pets_allowed || false
          },
          long_term_discounts: (data.long_term_discounts || []).map((d: any) => ({
            weeks: d.weeks,
            discount_rate: d.rate
          })),
          immediate_discounts: (data.immediate_discounts || []).map((d: any) => ({
            days: d.days,
            discount_amount: d.amount
          })),
          schedule: {
            min_contract_weeks: data.min_contract_weeks,
            available_from: data.available_from,
            available_until: data.available_until
          }
        };
        setSelectedProperty(detailProperty);
      })
      .catch((err) => {
        console.error('Failed to fetch property detail:', err);
        setError('매물 상세 정보를 불러오는데 실패했습니다.');
      });
  };

  useEffect(() => {
    if (user) fetchProperties();
  }, [user]);

  // 탭 전환 시 선택된 매물 초기화
  useEffect(() => {
    setSelectedProperty(null);
  }, [activeSection]);

  const formatPrice = (price: number | undefined) => {
    if (price === undefined || price === null) {
      return '0';
    }
    return price.toLocaleString('ko-KR');
  };

  const handleRegistrationSuccess = () => {
    fetchProperties();
    fetchReviewProperties();
  };

  const toggleDeleteSelection = (propertyId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedForDelete(prev => {
      const next = new Set(prev);
      if (next.has(propertyId)) {
        next.delete(propertyId);
      } else {
        next.add(propertyId);
      }
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedForDelete.size === 0) return;
    if (!window.confirm(`선택한 ${selectedForDelete.size}개의 매물을 삭제하시겠습니까?`)) return;

    try {
      const deletePromises = Array.from(selectedForDelete).map(id =>
        fetch(`${API_BASE_URL}/api/properties/${id}/status`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ status: 'deleted' })
        })
      );
      await Promise.all(deletePromises);
      setDeleteMode(false);
      setSelectedForDelete(new Set());
      fetchProperties();
      fetchReviewProperties();
    } catch (error) {
      console.error('Failed to delete properties:', error);
      alert('매물 삭제 중 오류가 발생했습니다.');
    }
  };

  const requestReview = async (propertyId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ status: '심사중' })
      });

      if (response.ok) {
        alert('심사 요청이 완료되었습니다.');
        fetchProperties(); // 목록 새로고침
        setSelectedProperty(null); // 상세화면 닫기
      } else {
        alert('심사 요청에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to request review:', error);
      alert('심사 요청 중 오류가 발생했습니다.');
    }
  };

  const approveProperty = async (propertyId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ status: '검수승인' })
      });

      if (response.ok) {
        alert('매물이 승인되었습니다.');
        fetchReviewProperties();
        fetchCompletedProperties();
        setSelectedProperty(null); // 상세화면 닫기
      } else {
        alert('승인에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to approve property:', error);
      alert('승인 중 오류가 발생했습니다.');
    }
  };

  const changePropertyStatus = async (propertyId: number, newStatus: string) => {
    const labelMap: Record<string, string> = {
      '심사중': '심사중', '검수승인': '승인', '검수반려': '반려', '검수보완': '보완요청'
    };
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        alert(`매물이 '${labelMap[newStatus] || newStatus}' 상태로 변경되었습니다.`);
        fetchReviewProperties();
        fetchCompletedProperties();
      } else {
        alert('상태 변경에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to change status:', error);
      alert('상태 변경 중 오류가 발생했습니다.');
    }
  };

  const rejectProperty = async (propertyId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ status: '검수반려' })
      });

      if (response.ok) {
        alert('매물이 거절되었습니다.');
        fetchReviewProperties();
        fetchCompletedProperties();
        setSelectedProperty(null); // 상세화면 닫기
      } else {
        alert('거절에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to reject property:', error);
      alert('거절 중 오류가 발생했습니다.');
    }
  };

  const aiReviewProperty = async (propertyId: number) => {
    try {
      setAiReviewing(true);
      // AI 심사 요청
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/ai-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
      });

      if (!response.ok) {
        alert('AI 심사 요청에 실패했습니다.');
        return;
      }

      const result = await response.json();
      setAiReviewResult(result);
      setShowAiReviewModal(true);

    } catch (error) {
      console.error('Failed to AI review property:', error);
      alert('AI 심사 중 오류가 발생했습니다.');
    } finally {
      setAiReviewing(false);
    }
  };

  const handleAiReviewConfirm = async () => {
    if (!aiReviewResult || !selectedProperty) return;

    try {
      // 상태 변경 API 호출
      const statusResponse = await fetch(`${API_BASE_URL}/api/properties/${selectedProperty.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ status: aiReviewResult.status })
      });

      if (statusResponse.ok) {
        const statusLabel = aiReviewResult.status === '검수승인' ? '승인' : aiReviewResult.status === '검수반려' ? '반려' : '보완요청';
        alert(`매물이 ${statusLabel} 처리되었습니다.`);
        fetchReviewProperties();
        fetchCompletedProperties();
        setSelectedProperty(null);
        setShowAiReviewModal(false);
        setAiReviewResult(null);
      } else {
        alert('상태 변경에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('상태 변경 중 오류가 발생했습니다.');
    }
  };

  // 공통 input 스타일
  const landingInputStyle: React.CSSProperties = {
    width: '100%', padding: isMobile ? '11px 12px' : '12px 14px',
    border: '1.5px solid #e2e8f0', borderRadius: 10,
    fontSize: isMobile ? 16 : 14, boxSizing: 'border-box',
    marginBottom: 12, outline: 'none',
  };
  const landingBtnStyle: React.CSSProperties = {
    width: '100%', padding: isMobile ? '14px' : '13px',
    backgroundColor: '#2563eb', color: '#fff',
    border: 'none', borderRadius: 10,
    fontSize: isMobile ? 16 : 15, fontWeight: 700, cursor: 'pointer', marginTop: 4,
  };
  const modalBoxStyle: React.CSSProperties = {
    backgroundColor: '#fff', borderRadius: isMobile ? 16 : 20,
    padding: isMobile ? '32px 24px' : '44px 40px',
    width: isMobile ? 'calc(100vw - 32px)' : 400,
    maxWidth: 420,
    boxShadow: '0 24px 60px rgba(0,0,0,0.2)',
  };

  // 곰 마스코트 SVG 컴포넌트
  const BearMascot = () => (
    <svg viewBox="0 0 320 300" width="320" height="300" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="bearGlow" cx="50%" cy="70%" r="50%">
          <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.18"/>
          <stop offset="100%" stopColor="#f59e0b" stopOpacity="0"/>
        </radialGradient>
      </defs>
      {/* Warm glow */}
      <ellipse cx="160" cy="220" rx="130" ry="80" fill="url(#bearGlow)"/>

      {/* 왼쪽 곰 */}
      <ellipse cx="95" cy="190" rx="52" ry="60" fill="#7A5230"/>
      <circle cx="65" cy="78" r="20" fill="#6B4826"/>
      <circle cx="65" cy="78" r="12" fill="#C68642"/>
      <circle cx="120" cy="78" r="20" fill="#6B4826"/>
      <circle cx="120" cy="78" r="12" fill="#C68642"/>
      <ellipse cx="95" cy="115" rx="44" ry="42" fill="#A07850"/>
      <ellipse cx="73" cy="128" rx="13" ry="8" fill="#E8926A" opacity="0.5"/>
      <ellipse cx="117" cy="128" rx="13" ry="8" fill="#E8926A" opacity="0.5"/>
      <ellipse cx="82" cy="110" rx="5" ry="6" fill="#1a1a1a"/>
      <ellipse cx="108" cy="110" rx="5" ry="6" fill="#1a1a1a"/>
      <circle cx="84" cy="108" r="2" fill="white"/>
      <circle cx="110" cy="108" r="2" fill="white"/>
      <ellipse cx="95" cy="122" rx="7" ry="5" fill="#4A2E0A"/>
      <path d="M87,130 Q95,138 103,130" stroke="#4A2E0A" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
      <ellipse cx="148" cy="185" rx="18" ry="10" fill="#7A5230" transform="rotate(-20 148 185)"/>

      {/* 오른쪽 곰 */}
      <ellipse cx="225" cy="190" rx="52" ry="60" fill="#6B4420"/>
      <circle cx="196" cy="78" r="20" fill="#5C3A18"/>
      <circle cx="196" cy="78" r="12" fill="#B07830"/>
      <circle cx="252" cy="78" r="20" fill="#5C3A18"/>
      <circle cx="252" cy="78" r="12" fill="#B07830"/>
      <ellipse cx="225" cy="115" rx="44" ry="42" fill="#906838"/>
      <ellipse cx="203" cy="128" rx="13" ry="8" fill="#E8926A" opacity="0.5"/>
      <ellipse cx="247" cy="128" rx="13" ry="8" fill="#E8926A" opacity="0.5"/>
      <ellipse cx="212" cy="110" rx="5" ry="6" fill="#1a1a1a"/>
      <ellipse cx="238" cy="110" rx="5" ry="6" fill="#1a1a1a"/>
      <circle cx="214" cy="108" r="2" fill="white"/>
      <circle cx="240" cy="108" r="2" fill="white"/>
      <ellipse cx="225" cy="122" rx="7" ry="5" fill="#3A2008"/>
      <path d="M217,130 Q225,138 233,130" stroke="#3A2008" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
      <ellipse cx="172" cy="185" rx="18" ry="10" fill="#6B4420" transform="rotate(20 172 185)"/>

      {/* 팻말 */}
      <rect x="100" y="210" width="120" height="72" rx="8" fill="#1e293b"/>
      <rect x="100" y="210" width="120" height="72" rx="8" fill="none" stroke="rgba(245,158,11,0.5)" strokeWidth="1.5"/>
      <text x="160" y="230" textAnchor="middle" fill="white" fontSize="11" fontStyle="italic" fontFamily="serif">Welcome</text>
      <text x="160" y="247" textAnchor="middle" fill="#94a3b8" fontSize="10" fontFamily="sans-serif">from the</text>
      <text x="160" y="268" textAnchor="middle" fill="#fbbf24" fontSize="12" fontWeight="bold" fontFamily="sans-serif">Happy Hosts!</text>
    </svg>
  );

  // 랜딩 페이지 (비로그인 상태)
  if (!user) {
    return (
      <div style={{ minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

        {/* ── 상단 네비게이션 ── */}
        <nav style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
          backgroundColor: 'rgba(15,23,42,0.96)', backdropFilter: 'blur(12px)',
          padding: isMobile ? '0 16px' : '0 40px', height: isMobile ? 56 : 64, display: 'flex', alignItems: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          <span style={{ color: '#fff', fontSize: isMobile ? 16 : 18, fontWeight: 800, letterSpacing: -0.5 }}>
            STR <span style={{ color: '#60a5fa', fontWeight: 400, fontSize: isMobile ? 11 : 13 }}>단기임대 관리</span>
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: isMobile ? 6 : 8, alignItems: 'center' }}>
            <button
              onClick={() => { setShowLoginModal(true); setShowSignupModal(false); }}
              style={{ color: '#cbd5e1', background: 'none', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, fontSize: isMobile ? 13 : 14, cursor: 'pointer', padding: isMobile ? '7px 14px' : '8px 18px' }}
            >
              로그인
            </button>
            <button
              onClick={() => { setShowSignupModal(true); setShowLoginModal(false); }}
              style={{ backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: isMobile ? '8px 16px' : '9px 20px', fontSize: isMobile ? 13 : 14, fontWeight: 600, cursor: 'pointer' }}
            >
              무료로 시작하기
            </button>
          </div>
        </nav>

        {/* ── 히어로 섹션 ── */}
        <section style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 55%, #0f2942 100%)',
          minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: isMobile ? '80px 20px 40px' : '80px 24px',
          position: 'relative' as const, overflow: 'hidden',
        }}>
          {/* 오로라 블롭 — 파란색 좌상단 */}
          <div style={{ position: 'absolute', top: -100, left: -100, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)', filter: 'blur(80px)', pointerEvents: 'none' as const }} />
          {/* 오로라 블롭 — 앰버 우하단 */}
          <div style={{ position: 'absolute', bottom: -50, right: -80, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(245,158,11,0.08) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' as const }} />
          {/* 도트 그리드 오버레이 */}
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' as const, backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)', backgroundSize: '32px 32px', maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent)', WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent)' }} />

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexDirection: isMobile ? 'column-reverse' : 'row',
            gap: isMobile ? 32 : 64, maxWidth: 1100, margin: '0 auto', width: '100%',
            flexWrap: 'wrap', position: 'relative' as const, zIndex: 1,
          }}>
            {/* 곰 캐릭터 — 플로팅 애니메이션 */}
            <div className="float-bear" style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', transform: isMobile ? 'scale(0.65)' : 'none' }}>
              <BearMascot />
            </div>

            {/* 텍스트 + CTA */}
            <div style={{ flex: 1, minWidth: isMobile ? 0 : 300, maxWidth: isMobile ? '100%' : 520, textAlign: isMobile ? 'center' : 'left' }}>
              <div style={{
                display: 'inline-block', backgroundColor: 'rgba(245,158,11,0.15)',
                border: '1px solid rgba(245,158,11,0.35)', borderRadius: 20,
                padding: isMobile ? '5px 14px' : '6px 18px', marginBottom: isMobile ? 20 : 28,
              }}>
                <span style={{ color: '#fbbf24', fontSize: isMobile ? 11 : 13, fontWeight: 500 }}>단기임대 호스트 전용 관리 솔루션</span>
              </div>
              <h1 style={{
                color: '#fff', fontSize: isMobile ? 30 : isTablet ? 40 : 52, fontWeight: 800, lineHeight: 1.2,
                margin: '0 0 20px', letterSpacing: isMobile ? -0.5 : -1.5,
              }}>
                단기임대 예약·정산을<br />
                <span style={{ background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 50%, #fbbf24 100%)', backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'gradientShift 3s linear infinite' }}>한 곳에서</span> 관리하세요
              </h1>
              <p style={{ color: '#94a3b8', fontSize: isMobile ? 15 : 18, margin: isMobile ? '0 0 32px' : '0 0 44px', lineHeight: 1.75 }}>
                복잡한 단기임대 운영을 한 화면에서 처리하세요.<br />
                공동관리로 함께 운영도 가능합니다.
              </p>
              <div style={{ display: 'flex', gap: isMobile ? 10 : 14, flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-start' }}>
                <button
                  onClick={() => { setShowSignupModal(true); setShowLoginModal(false); }}
                  style={{
                    background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                    color: '#fff', border: 'none',
                    borderRadius: 12, padding: isMobile ? '14px 28px' : '16px 40px', fontSize: isMobile ? 15 : 16, fontWeight: 700, cursor: 'pointer',
                    animation: 'ctaPulse 2.5s ease-in-out infinite',
                  }}
                >
                  지금 시작하기 →
                </button>
                <button
                  onClick={() => { setShowLoginModal(true); setShowSignupModal(false); }}
                  style={{
                    backgroundColor: 'transparent', color: '#e2e8f0',
                    border: '1.5px solid rgba(255,255,255,0.25)', borderRadius: 12,
                    padding: isMobile ? '14px 24px' : '16px 36px', fontSize: isMobile ? 15 : 16, cursor: 'pointer',
                  }}
                >
                  로그인
                </button>
              </div>

              {/* 미니 스탯 카드 (글래스모피즘) */}
              <div style={{ display: 'flex', gap: isMobile ? 10 : 16, marginTop: isMobile ? 28 : 36, flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-start' }}>
                {[{ num: '100%', label: '무료 시작' }, { num: '3분', label: '설정 완료' }, { num: '24/7', label: '실시간 계산' }].map(s => (
                  <div key={s.label} style={{
                    background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10,
                    padding: isMobile ? '10px 16px' : '12px 20px', textAlign: 'center' as const,
                  }}>
                    <div style={{ color: '#60a5fa', fontSize: isMobile ? 17 : 20, fontWeight: 800 }}>{s.num}</div>
                    <div style={{ color: '#94a3b8', fontSize: isMobile ? 10 : 11, marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </section>

        {/* ── 피처 카드 섹션 (다크 테마 + 글래스모피즘) ── */}
        <section className="fade-in-section" style={{ backgroundColor: '#0b1120', padding: isMobile ? '56px 20px' : '88px 24px' }}>
          <div style={{ maxWidth: 960, margin: '0 auto' }}>
            <h2 style={{
              textAlign: 'center', fontSize: isMobile ? 24 : 32, fontWeight: 700, color: '#fff',
              margin: '0 0 12px', letterSpacing: -0.5,
            }}>
              호스트 운영의 모든 것
            </h2>
            <p style={{ textAlign: 'center', color: '#64748b', fontSize: isMobile ? 14 : 16, margin: isMobile ? '0 0 36px' : '0 0 56px' }}>
              복잡한 스프레드시트 없이, 예약부터 정산까지 한눈에
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? 16 : 24 }}>
              {[
                { icon: '📅', title: '예약 관리', desc: '체크인·아웃 일정, 게스트 정보, 채널별 예약을 달력과 리스트로 한눈에 확인하세요.' },
                { icon: '💰', title: '정산 관리', desc: '숙박비·관리비·청소비 자동 계산. 플랫폼 수수료 차감 후 실수익을 즉시 확인하세요.' },
                { icon: '📊', title: '운영 현황', desc: '가동률, 누적 정산액을 방별로 분석해 수익성을 빠르게 파악하세요.' },
                { icon: '👥', title: '공동관리', desc: '여러 호스트가 하나의 매물을 함께 관리. 권한 공유로 팀 운영이 간편해집니다.' },
              ].map((f) => (
                <div
                  key={f.title}
                  onMouseEnter={() => setHoveredCard(f.title)}
                  onMouseLeave={() => setHoveredCard(null)}
                  style={{
                    background: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
                    borderRadius: isMobile ? 12 : 16, padding: isMobile ? '24px 20px' : '32px 28px',
                    boxShadow: hoveredCard === f.title ? '0 12px 40px rgba(37,99,235,0.15)' : '0 4px 24px rgba(0,0,0,0.3)',
                    border: hoveredCard === f.title ? '1px solid rgba(59,130,246,0.3)' : '1px solid rgba(255,255,255,0.08)',
                    transform: hoveredCard === f.title ? 'translateY(-6px)' : 'none',
                    transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
                    cursor: 'default',
                  }}
                >
                  <div style={{
                    width: isMobile ? 44 : 52, height: isMobile ? 44 : 52,
                    background: 'linear-gradient(135deg, rgba(37,99,235,0.15), rgba(59,130,246,0.25))',
                    borderRadius: isMobile ? 12 : 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: isMobile ? 22 : 26, marginBottom: isMobile ? 14 : 18,
                  }}>{f.icon}</div>
                  <h3 style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#e2e8f0', margin: '0 0 10px' }}>{f.title}</h3>
                  <p style={{ fontSize: isMobile ? 13 : 14, color: '#64748b', lineHeight: 1.75, margin: 0 }}>{f.desc}</p>
                </div>
              ))}
            </div>

            {/* CTA 배너 */}
            <div className="fade-in-section" style={{
              marginTop: isMobile ? 40 : 64, background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)',
              borderRadius: isMobile ? 14 : 20, padding: isMobile ? '32px 20px' : '48px 40px', textAlign: 'center',
              border: '1px solid rgba(255,255,255,0.08)',
            }}>
              <h3 style={{ color: '#fff', fontSize: isMobile ? 20 : 26, fontWeight: 700, margin: '0 0 12px' }}>
                지금 바로 시작해보세요
              </h3>
              <p style={{ color: '#94a3b8', fontSize: isMobile ? 13 : 15, margin: '0 0 28px' }}>무료로 가입하고 단기임대 관리를 시작하세요</p>
              <button
                onClick={() => { setShowSignupModal(true); setShowLoginModal(false); }}
                style={{
                  backgroundColor: '#2563eb', color: '#fff', border: 'none',
                  borderRadius: 10, padding: isMobile ? '13px 28px' : '14px 36px', fontSize: isMobile ? 14 : 15, fontWeight: 700, cursor: 'pointer',
                  animation: 'ctaPulse 2.5s ease-in-out infinite',
                }}
              >
                30초 회원가입
              </button>
            </div>
          </div>
        </section>

        {/* ── 푸터 ── */}
        <footer style={{ backgroundColor: '#0f172a', padding: isMobile ? '20px 16px' : '28px 40px', textAlign: 'center' }}>
          <span style={{ color: '#475569', fontSize: 13 }}>© 2026 STR 단기임대 관리. All rights reserved.</span>
        </footer>

        {/* ── 로그인 모달 ── */}
        {showLoginModal && (
          <div
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={() => setShowLoginModal(false)}
          >
            <div
              style={modalBoxStyle}
              onClick={e => e.stopPropagation()}
            >
              <h2 style={{ margin: '0 0 30px', fontSize: isMobile ? 22 : 24, fontWeight: 800, textAlign: 'center', color: '#0f172a' }}>로그인</h2>
              <input
                placeholder="이메일 또는 휴대폰번호"
                value={loginIdentifier}
                onChange={e => setLoginIdentifier(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleLogin(); }}
                style={landingInputStyle}
              />
              <input
                type="password"
                placeholder="비밀번호"
                value={loginPassword}
                onChange={e => setLoginPassword(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleLogin(); }}
                style={landingInputStyle}
              />
              {loginError && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 10 }}>{loginError}</div>}
              <button onClick={handleLogin} style={landingBtnStyle}>로그인</button>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14, fontSize: 13 }}>
                <span
                  onClick={() => { setShowLoginModal(false); setShowForgotModal(true); setLoginError(''); }}
                  style={{ color: '#94a3b8', cursor: 'pointer' }}
                >
                  비밀번호 찾기
                </span>
                <span style={{ color: '#94a3b8' }}>
                  계정이 없으신가요?{' '}
                  <span
                    onClick={() => { setShowLoginModal(false); setShowSignupModal(true); setLoginError(''); }}
                    style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}
                  >
                    회원가입
                  </span>
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ── 회원가입 모달 ── */}
        {showSignupModal && (
          <div
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={() => setShowSignupModal(false)}
          >
            <div
              style={{ ...modalBoxStyle, width: isMobile ? 'calc(100vw - 32px)' : 420 }}
              onClick={e => e.stopPropagation()}
            >
              <h2 style={{ margin: '0 0 30px', fontSize: isMobile ? 22 : 24, fontWeight: 800, textAlign: 'center', color: '#0f172a' }}>회원가입</h2>
              <input
                placeholder="이름 *"
                value={signupName}
                onChange={e => setSignupName(e.target.value)}
                autoComplete="off"
                style={landingInputStyle}
              />
              <input
                placeholder="이메일 *"
                type="email"
                value={signupEmail}
                onChange={e => setSignupEmail(e.target.value)}
                autoComplete="off"
                style={landingInputStyle}
              />
              <input
                placeholder="휴대폰번호 *"
                type="tel"
                value={signupPhone}
                onChange={e => {
                  const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 11);
                  if (digits.length <= 3) setSignupPhone(digits);
                  else if (digits.length <= 7) setSignupPhone(`${digits.slice(0, 3)}-${digits.slice(3)}`);
                  else setSignupPhone(`${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`);
                }}
                autoComplete="off"
                style={landingInputStyle}
              />
              <input
                type="password"
                placeholder="비밀번호 *"
                value={signupPassword}
                onChange={e => setSignupPassword(e.target.value)}
                autoComplete="new-password"
                style={landingInputStyle}
              />
              <input
                type="password"
                placeholder="비밀번호 확인 *"
                value={signupPasswordConfirm}
                onChange={e => setSignupPasswordConfirm(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSignup(); }}
                autoComplete="new-password"
                style={{ ...landingInputStyle, marginBottom: 4 }}
              />
              {signupError && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 10 }}>{signupError}</div>}
              <button onClick={handleSignup} style={{ ...landingBtnStyle, marginTop: 8 }}>가입하기</button>
              <p style={{ margin: '18px 0 0', textAlign: 'center', fontSize: 13, color: '#94a3b8' }}>
                이미 계정이 있으신가요?{' '}
                <span
                  onClick={() => { setShowSignupModal(false); setShowLoginModal(true); setSignupError(''); }}
                  style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}
                >
                  로그인
                </span>
              </p>
            </div>
          </div>
        )}

        {/* ── 비밀번호 찾기 모달 ── */}
        {showForgotModal && (
          <div
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={() => setShowForgotModal(false)}
          >
            <div
              style={modalBoxStyle}
              onClick={e => e.stopPropagation()}
            >
              <h2 style={{ margin: '0 0 8px', fontSize: isMobile ? 22 : 24, fontWeight: 800, textAlign: 'center', color: '#0f172a' }}>비밀번호 찾기</h2>

              {forgotStep === 'email' ? (
                <>
                  <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 24px', textAlign: 'center' }}>
                    가입한 이메일 주소를 입력하면 인증 코드를 보내드립니다.
                  </p>
                  <input
                    placeholder="이메일 *"
                    type="email"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleForgotPassword(); }}
                    style={landingInputStyle}
                  />
                  {forgotError && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 10 }}>{forgotError}</div>}
                  <button onClick={handleForgotPassword} style={landingBtnStyle}>인증 코드 전송</button>
                </>
              ) : (
                <>
                  {forgotMessage && <p style={{ color: '#16a34a', fontSize: 13, margin: '0 0 16px', textAlign: 'center' }}>{forgotMessage}</p>}
                  <input
                    placeholder="6자리 인증 코드 *"
                    value={forgotCode}
                    onChange={e => setForgotCode(e.target.value)}
                    style={landingInputStyle}
                  />
                  <input
                    type="password"
                    placeholder="새 비밀번호 *"
                    value={forgotNewPw}
                    onChange={e => setForgotNewPw(e.target.value)}
                    style={landingInputStyle}
                  />
                  <input
                    type="password"
                    placeholder="새 비밀번호 확인 *"
                    value={forgotNewPwConfirm}
                    onChange={e => setForgotNewPwConfirm(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleResetPassword(); }}
                    style={{ ...landingInputStyle, marginBottom: 4 }}
                  />
                  {forgotError && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 10 }}>{forgotError}</div>}
                  <button onClick={handleResetPassword} style={landingBtnStyle}>비밀번호 재설정</button>
                  <div style={{ textAlign: 'center', marginTop: 10 }}>
                    <span
                      onClick={() => setForgotStep('email')}
                      style={{ color: '#94a3b8', cursor: 'pointer', fontSize: 13 }}
                    >
                      ← 이메일 다시 입력
                    </span>
                  </div>
                </>
              )}

              <p style={{ margin: '18px 0 0', textAlign: 'center', fontSize: 13, color: '#94a3b8' }}>
                <span
                  onClick={() => { setShowForgotModal(false); setShowLoginModal(true); setForgotError(''); setForgotStep('email'); }}
                  style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}
                >
                  로그인으로 돌아가기
                </span>
              </p>
            </div>
          </div>
        )}

      </div>
    );
  }

  return (
    <div className="App">
      {/* 네비게이션 바 */}
      <nav style={{
        backgroundColor: '#fff',
        borderBottom: '2px solid #e0e0e0',
        position: 'sticky',
        top: 0,
        zIndex: 999
      }}>
        <div className={isMobile ? 'nav-scroll' : ''} style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: isMobile ? '0 8px' : '0 20px',
          display: 'flex',
          alignItems: 'center',
          gap: '0',
          overflowX: isMobile ? 'auto' : 'visible',
        }}>
          <button
            onClick={() => setActiveSection('rooms')}
            style={{
              padding: isMobile ? '12px 14px' : '20px 30px',
              border: 'none',
              background: 'none',
              fontSize: isMobile ? '13px' : '16px',
              whiteSpace: 'nowrap' as const, flexShrink: 0,
              fontWeight: activeSection === 'rooms' ? 'bold' : 'normal',
              color: activeSection === 'rooms' ? '#000' : '#666',
              cursor: 'pointer',
              borderBottom: activeSection === 'rooms' ? '3px solid #000' : '3px solid transparent',
              transition: 'all 0.2s'
            }}
          >
            예약/정산관리
          </button>
          <button
            onClick={() => setActiveSection('stats')}
            style={{
              padding: isMobile ? '12px 14px' : '20px 30px',
              border: 'none',
              background: 'none',
              fontSize: isMobile ? '13px' : '16px',
              whiteSpace: 'nowrap' as const, flexShrink: 0,
              fontWeight: activeSection === 'stats' ? 'bold' : 'normal',
              color: activeSection === 'stats' ? '#000' : '#666',
              cursor: 'pointer',
              borderBottom: activeSection === 'stats' ? '3px solid #000' : '3px solid transparent',
              transition: 'all 0.2s'
            }}
          >
            통계/데이터
          </button>
          <button
            onClick={() => setActiveSection('notices')}
            style={{
              padding: isMobile ? '12px 14px' : '20px 30px',
              border: 'none',
              background: 'none',
              fontSize: isMobile ? '13px' : '16px',
              whiteSpace: 'nowrap' as const, flexShrink: 0,
              fontWeight: activeSection === 'notices' ? 'bold' : 'normal',
              color: activeSection === 'notices' ? '#000' : '#666',
              cursor: 'pointer',
              borderBottom: activeSection === 'notices' ? '3px solid #000' : '3px solid transparent',
              transition: 'all 0.2s'
            }}
          >
            정보/소식
          </button>
          {user.role === 'admin' && (
            <>
              <button
                onClick={() => setActiveSection('registration')}
                style={{
                  padding: isMobile ? '12px 14px' : '20px 30px',
                  border: 'none',
                  background: 'none',
                  fontSize: isMobile ? '13px' : '16px',
                  whiteSpace: 'nowrap' as const, flexShrink: 0,
                  fontWeight: activeSection === 'registration' ? 'bold' : 'normal',
                  color: activeSection === 'registration' ? '#000' : '#666',
                  cursor: 'pointer',
                  borderBottom: activeSection === 'registration' ? '3px solid #000' : '3px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                방등록
              </button>
              <button
                onClick={() => setActiveSection('admin')}
                style={{
                  padding: isMobile ? '12px 14px' : '20px 30px',
                  border: 'none',
                  background: 'none',
                  fontSize: isMobile ? '13px' : '16px',
                  whiteSpace: 'nowrap' as const, flexShrink: 0,
                  fontWeight: activeSection === 'admin' ? 'bold' : 'normal',
                  color: activeSection === 'admin' ? '#000' : '#666',
                  cursor: 'pointer',
                  borderBottom: activeSection === 'admin' ? '3px solid #000' : '3px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                어드민
              </button>
              <button
                onClick={() => {
                  setActiveSection('review');
                  fetchReviewProperties();
                }}
                style={{
                  padding: isMobile ? '12px 14px' : '20px 30px',
                  border: 'none',
                  background: 'none',
                  fontSize: isMobile ? '13px' : '16px',
                  whiteSpace: 'nowrap' as const, flexShrink: 0,
                  fontWeight: activeSection === 'review' ? 'bold' : 'normal',
                  color: activeSection === 'review' ? '#000' : '#666',
                  cursor: 'pointer',
                  borderBottom: activeSection === 'review' ? '3px solid #000' : '3px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                방심사
              </button>
              <button
                onClick={() => setActiveSection('criteria')}
                style={{
                  padding: isMobile ? '12px 14px' : '20px 30px',
                  border: 'none',
                  background: 'none',
                  fontSize: isMobile ? '13px' : '16px',
                  whiteSpace: 'nowrap' as const, flexShrink: 0,
                  fontWeight: activeSection === 'criteria' ? 'bold' : 'normal',
                  color: activeSection === 'criteria' ? '#000' : '#666',
                  cursor: 'pointer',
                  borderBottom: activeSection === 'criteria' ? '3px solid #000' : '3px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                검수 기준
              </button>
              <button
                onClick={() => {
                  setActiveSection('completed');
                  fetchCompletedProperties();
                }}
                style={{
                  padding: isMobile ? '12px 14px' : '20px 30px',
                  border: 'none',
                  background: 'none',
                  fontSize: isMobile ? '13px' : '16px',
                  whiteSpace: 'nowrap' as const, flexShrink: 0,
                  fontWeight: activeSection === 'completed' ? 'bold' : 'normal',
                  color: activeSection === 'completed' ? '#000' : '#666',
                  cursor: 'pointer',
                  borderBottom: activeSection === 'completed' ? '3px solid #000' : '3px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                검수 완료
              </button>
              <button
                onClick={() => {
                  setActiveSection('exposure');
                  fetchExposedProperties();
                }}
                style={{
                  padding: isMobile ? '12px 14px' : '20px 30px',
                  border: 'none',
                  background: 'none',
                  fontSize: isMobile ? '13px' : '16px',
                  whiteSpace: 'nowrap' as const, flexShrink: 0,
                  fontWeight: activeSection === 'exposure' ? 'bold' : 'normal',
                  color: activeSection === 'exposure' ? '#000' : '#666',
                  cursor: 'pointer',
                  borderBottom: activeSection === 'exposure' ? '3px solid #000' : '3px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                방 노출
              </button>
            </>
          )}
          <button
            onClick={() => { setActiveSection('inquiry'); setHasNewInquiry(false); }}
            style={{
              padding: isMobile ? '12px 14px' : '20px 30px',
              border: 'none',
              background: 'none',
              fontSize: isMobile ? '13px' : '16px',
              whiteSpace: 'nowrap' as const, flexShrink: 0,
              fontWeight: activeSection === 'inquiry' ? 'bold' : 'normal',
              color: activeSection === 'inquiry' ? '#000' : '#666',
              cursor: 'pointer',
              borderBottom: activeSection === 'inquiry' ? '3px solid #000' : '3px solid transparent',
              transition: 'all 0.2s',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            문의/요청
            {hasNewInquiry && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 16, height: 16, background: '#ef4444', color: '#fff',
                borderRadius: '50%', fontSize: 10, fontWeight: 700, lineHeight: 1,
              }}>N</span>
            )}
          </button>
          <button
            onClick={() => { setActiveSection('updates'); setHasNewUpdate(false); }}
            style={{
              padding: isMobile ? '12px 14px' : '20px 30px',
              border: 'none',
              background: 'none',
              fontSize: isMobile ? '13px' : '16px',
              whiteSpace: 'nowrap' as const, flexShrink: 0,
              fontWeight: activeSection === 'updates' ? 'bold' : 'normal',
              color: activeSection === 'updates' ? '#000' : '#666',
              cursor: 'pointer',
              borderBottom: activeSection === 'updates' ? '3px solid #000' : '3px solid transparent',
              transition: 'all 0.2s',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            업데이트
            {hasNewUpdate && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 16, height: 16, background: '#ef4444', color: '#fff',
                borderRadius: '50%', fontSize: 10, fontWeight: 700, lineHeight: 1,
              }}>N</span>
            )}
          </button>
          <button
            onClick={() => setActiveSection('transfers')}
            style={{
              padding: isMobile ? '12px 14px' : '20px 30px',
              border: 'none',
              background: 'none',
              fontSize: isMobile ? '13px' : '16px',
              whiteSpace: 'nowrap' as const, flexShrink: 0,
              fontWeight: activeSection === 'transfers' ? 'bold' : 'normal',
              color: activeSection === 'transfers' ? '#000' : '#666',
              cursor: 'pointer',
              borderBottom: activeSection === 'transfers' ? '3px solid #000' : '3px solid transparent',
              transition: 'all 0.2s'
            }}
          >
            양도/양수
          </button>
          {user.role === 'admin' && (
            <button
              onClick={() => setActiveSection('admin2')}
              style={{
                padding: isMobile ? '12px 14px' : '20px 30px',
                border: 'none',
                background: 'none',
                fontSize: isMobile ? '13px' : '16px',
                whiteSpace: 'nowrap' as const, flexShrink: 0,
                fontWeight: activeSection === 'admin2' ? 'bold' : 'normal',
                color: activeSection === 'admin2' ? '#000' : '#666',
                cursor: 'pointer',
                borderBottom: activeSection === 'admin2' ? '3px solid #000' : '3px solid transparent',
                transition: 'all 0.2s'
              }}
            >
              어드민2
            </button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, paddingRight: 4, flexShrink: 0 }}>
            <div data-user-menu style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 8 }}>
              <span
                onClick={(e) => { e.stopPropagation(); setShowUserMenu(v => !v); setGrantMessage(''); }}
                style={{ fontSize: 13, color: '#666', cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                {user.name}
                {user.role === 'admin' && (
                  <span style={{ fontSize: 10, backgroundColor: '#6B4EFF', color: '#fff', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>관리자</span>
                )}
              </span>
              <button
                onClick={handleLogout}
                style={{ marginLeft: 8, fontSize: 12, color: '#94a3b8', background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 10px', cursor: 'pointer' }}
              >
                로그아웃
              </button>
              {showUserMenu && (
                <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 8, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: 16, minWidth: 280, zIndex: 9999 }}>
                  <p style={{ margin: '0 0 8px', fontSize: 13, color: '#333', fontWeight: 600 }}>{user.email}</p>
                  {user.role === 'admin' && (
                    <>
                      <div style={{ backgroundColor: '#f3f0ff', border: '1px solid #6B4EFF', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
                        <span style={{ fontSize: 12, color: '#6B4EFF', fontWeight: 700 }}>✓ 관리자 권한 있음</span>
                      </div>
                      <div>
                        <p style={{ margin: '0 0 6px', fontSize: 12, color: '#666', fontWeight: 600 }}>관리자 권한 부여</p>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input
                            type="email"
                            placeholder="이메일 주소 입력"
                            value={grantEmail}
                            onChange={e => setGrantEmail(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleGrantAdmin()}
                            style={{ flex: 1, padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12 }}
                          />
                          <button
                            onClick={handleGrantAdmin}
                            style={{ padding: '7px 12px', backgroundColor: '#6B4EFF', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            권한 부여
                          </button>
                        </div>
                        {grantMessage && (
                          <p style={{ margin: '6px 0 0', fontSize: 11, color: grantMessage.startsWith('✓') ? '#16a34a' : '#dc2626' }}>{grantMessage}</p>
                        )}
                      </div>
                    </>
                  )}
                  <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 12, paddingTop: 12 }}>
                    <p style={{ margin: '0 0 6px', fontSize: 12, color: '#666', fontWeight: 600 }}>비밀번호 변경</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <input
                        type="password"
                        placeholder="현재 비밀번호"
                        value={changePwCurrent}
                        onChange={e => setChangePwCurrent(e.target.value)}
                        style={{ padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12 }}
                      />
                      <input
                        type="password"
                        placeholder="새 비밀번호"
                        value={changePwNew}
                        onChange={e => setChangePwNew(e.target.value)}
                        style={{ padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12 }}
                      />
                      <input
                        type="password"
                        placeholder="새 비밀번호 확인"
                        value={changePwConfirm}
                        onChange={e => setChangePwConfirm(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleChangePassword()}
                        style={{ padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12 }}
                      />
                      <button
                        onClick={handleChangePassword}
                        style={{ padding: '7px 12px', backgroundColor: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
                      >
                        비밀번호 변경
                      </button>
                    </div>
                    {changePwError && (
                      <p style={{ margin: '6px 0 0', fontSize: 11, color: '#dc2626' }}>{changePwError}</p>
                    )}
                    {changePwMessage && (
                      <p style={{ margin: '6px 0 0', fontSize: 11, color: '#16a34a' }}>{changePwMessage}</p>
                    )}
                  </div>
                  <button
                    onClick={() => { handleLogout(); }}
                    style={{ marginTop: 12, width: '100%', padding: '8px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
                  >
                    로그아웃
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* 메인 컨텐츠 */}
      <div style={{ padding: (activeSection === 'registration' || activeSection === 'exposure') && !selectedProperty ? '0' : (activeSection === 'rooms' || activeSection === 'inquiry') && !selectedProperty ? (isMobile ? '0 8px' : '0 24px') : (isMobile ? '12px 8px' : '20px'), maxWidth: (activeSection === 'registration' || activeSection === 'exposure') && !selectedProperty ? '100%' : (activeSection === 'rooms' || activeSection === 'inquiry') && !selectedProperty ? (isMobile ? '100%' : '1058px') : '1200px', margin: '0 auto' }}>
        {/* 통계/데이터 (개발중) */}
        {activeSection === 'stats' && (
          <div style={{ height: 'calc(100vh - 65px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center', color: '#94a3b8' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#475569', marginBottom: 8 }}>통계 / 데이터</div>
              <div style={{ fontSize: 14 }}>개발중입니다</div>
            </div>
          </div>
        )}

        {/* 정보/소식 */}
        {activeSection === 'notices' && (
          <div style={{ height: 'calc(100vh - 65px)', overflow: 'auto' }}>
            <NoticeBoard token={token} user={user} />
          </div>
        )}

        {/* 양도/양수 */}
        {activeSection === 'transfers' && (
          <div style={{ height: 'calc(100vh - 65px)', overflow: 'auto' }}>
            <TransferBoard token={token} user={user} />
          </div>
        )}

        {/* 문의/요청 */}
        {activeSection === 'inquiry' && (
          <div style={{ height: 'calc(100vh - 65px)', overflow: 'auto' }}>
            <InquiryBoard token={token} user={user} />
          </div>
        )}

        {/* 업데이트 */}
        {activeSection === 'updates' && (
          <div style={{ height: 'calc(100vh - 65px)', overflow: 'auto' }}>
            <UpdateBoard token={token} user={user} />
          </div>
        )}

        {/* 방관리 */}
        {activeSection === 'rooms' && !selectedProperty && (
          <div style={{ height: 'calc(100vh - 65px)' }}>
            <RoomManagement token={token} user={user} />
          </div>
        )}

        {/* 방등록 (기존 매물 카드 리스트 + 지도) */}
        {activeSection === 'registration' && user.role === 'admin' && !selectedProperty && (
          <div style={{ display: 'flex', height: 'calc(100vh - 65px)' }}>
            {/* 왼쪽: 매물 카드 리스트 */}
            <div style={{
              width: '554px',
              minWidth: '554px',
              overflowY: 'auto',
              padding: '20px 12px 20px 12px',
              textAlign: 'left',
            }}>
              <div style={{ width: '530px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.4' }}>매물 {properties.length}개</h2>
                    <span style={{ fontSize: '11px', color: '#E53935', lineHeight: '1.4', paddingTop: '2px' }}>
                      - 등록된 모든 매물이 보여집니다.<br />검수가 완료 되면 방 노출에서 보여집니다.
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                  {deleteMode ? (
                    <>
                      <span style={{ fontSize: '13px', color: '#666' }}>
                        {selectedForDelete.size}개 선택
                      </span>
                      <button
                        onClick={handleDeleteSelected}
                        disabled={selectedForDelete.size === 0}
                        style={{
                          padding: '8px 18px',
                          backgroundColor: selectedForDelete.size > 0 ? '#E53935' : '#ccc',
                          color: 'white',
                          border: 'none',
                          borderRadius: '20px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          cursor: selectedForDelete.size > 0 ? 'pointer' : 'default',
                        }}
                      >
                        삭제
                      </button>
                      <button
                        onClick={() => { setDeleteMode(false); setSelectedForDelete(new Set()); }}
                        style={{
                          padding: '8px 18px',
                          backgroundColor: '#fff',
                          color: '#333',
                          border: '1px solid #ddd',
                          borderRadius: '20px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                        }}
                      >
                        취소
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setDeleteMode(true)}
                        style={{
                          padding: '8px 18px',
                          backgroundColor: '#fff',
                          color: '#333',
                          border: '1px solid #ddd',
                          borderRadius: '20px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                      >
                        방 삭제
                      </button>
                      <button
                        onClick={() => setShowRegistration(true)}
                        style={{
                          padding: '8px 18px',
                          backgroundColor: '#000',
                          color: 'white',
                          border: 'none',
                          borderRadius: '20px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#333'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#000'}
                      >
                        <span style={{ fontSize: '16px' }}>+</span> 방 등록
                      </button>
                    </>
                  )}
                  </div>
                </div>
              </div>

              {loading && <p style={{ textAlign: 'center', color: '#999' }}>로딩 중...</p>}
              {error && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 255px)',
                gap: '20px',
              }}>
                {properties.map(property => (
                  <div
                    key={property.id}
                    onClick={(e) => deleteMode ? toggleDeleteSelection(property.id, e) : fetchPropertyDetail(property.id)}
                    onMouseEnter={() => setHoveredPropertyId(property.id)}
                    onMouseLeave={() => setHoveredPropertyId(null)}
                    style={{
                      borderRadius: '12px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'box-shadow 0.2s',
                      boxShadow: hoveredPropertyId === property.id ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
                      outline: deleteMode && selectedForDelete.has(property.id) ? '3px solid #E53935' : 'none',
                    }}
                  >
                    {/* 삭제 모드 체크박스 */}
                    {deleteMode && (
                      <div
                        onClick={(e) => toggleDeleteSelection(property.id, e)}
                        style={{
                          position: 'absolute',
                          top: '10px',
                          left: '10px',
                          zIndex: 10,
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: selectedForDelete.has(property.id) ? '#E53935' : 'rgba(255,255,255,0.9)',
                          border: selectedForDelete.has(property.id) ? '2px solid #E53935' : '2px solid #ccc',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        {selectedForDelete.has(property.id) && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </div>
                    )}
                    {/* 사진 + 하트 아이콘 */}
                    <div style={{
                      width: '100%',
                      height: '200px',
                      backgroundColor: '#f0f0f0',
                      position: 'relative',
                      borderRadius: '12px',
                      overflow: 'hidden',
                    }}>
                      {property.photos && property.photos.length > 0 ? (
                        <img
                          src={convertImageUrl(property.photos[0].photo_url)}
                          alt={property.descriptions?.room_name || '매물 사진'}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#bbb',
                          fontSize: '13px',
                        }}>이미지 없음</div>
                      )}
                      {/* 하트 아이콘 */}
                      <div style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                      </div>
                    </div>

                    {/* 정보 */}
                    <div style={{ padding: '10px 4px' }}>
                      {/* 주소 + 건물유형 */}
                      <p style={{
                        margin: '0 0 2px 0',
                        fontSize: '15px',
                        fontWeight: '600',
                        color: '#222',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {property.space?.road_address
                          ? (() => {
                              const addr = property.space!.road_address;
                              const parts = addr.split(' ');
                              const city = parts[0] || '';
                              const dong = parts.length >= 3 ? parts[2] : parts[1] || '';
                              return `${city} ${dong}의 ${property.space!.building_type || ''}`;
                            })()
                          : '주소 미등록'}
                      </p>

                      {/* 방 설명 (한 줄) */}
                      <p style={{
                        margin: '0 0 4px 0',
                        fontSize: '13px',
                        color: '#888',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {property.descriptions?.room_name || ''}
                      </p>

                      {/* 공간 정보 */}
                      {property.space && (
                        <p style={{
                          margin: '0 0 6px 0',
                          fontSize: '13px',
                          color: '#888',
                        }}>
                          방{property.space.room_count}  화장실{property.space.bathroom_count}  주방{property.space.kitchen_count || 1}  거실{property.space.living_room_count || 1}
                        </p>
                      )}

                      {/* 가격 */}
                      {property.rent && (
                        <div style={{ marginBottom: '4px' }}>
                          <span style={{ fontSize: '18px', fontWeight: '700', color: '#222' }}>
                            {formatPrice(property.rent.weekly_rent)}원
                          </span>
                          <span style={{ fontSize: '14px', color: '#888', marginLeft: '2px' }}> / </span>
                          <span style={{ fontSize: '14px', fontWeight: '600', color: '#FF8C00' }}>1주</span>
                        </div>
                      )}

                      {/* 임대료/관리비 */}
                      {property.rent && (
                        <p style={{
                          margin: '0 0 8px 0',
                          fontSize: '12px',
                          color: '#999',
                        }}>
                          임대료 {formatPrice(property.rent.deposit)}원  관리비 {formatPrice(property.rent.weekly_maintenance_fee)}원
                        </p>
                      )}

                      {/* 할인 뱃지 */}
                      {(property.long_term_discounts?.length > 0 || property.immediate_discounts?.length > 0) && (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {property.long_term_discounts?.length > 0 && (
                            <span style={{
                              fontSize: '11px',
                              color: '#888',
                              padding: '3px 8px',
                              border: '1px solid #e0e0e0',
                              borderRadius: '4px',
                            }}>
                              장기계약 ~{property.long_term_discounts[0].discount_rate}% <span style={{ color: '#FF8C00' }}>할인</span>
                            </span>
                          )}
                          {property.immediate_discounts?.length > 0 && (
                            <span style={{
                              fontSize: '11px',
                              color: '#888',
                              padding: '3px 8px',
                              border: '1px solid #e0e0e0',
                              borderRadius: '4px',
                            }}>
                              즉시입주 ~{formatPrice(property.immediate_discounts[0].discount_amount)} <span style={{ color: '#FF8C00' }}>할인</span>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {properties.length === 0 && !loading && !error && (
                <p style={{ textAlign: 'center', color: '#666', marginTop: '60px' }}>등록된 매물이 없습니다.</p>
              )}
            </div>

            {/* 오른쪽: 지도 */}
            <div style={{
              flex: 1,
              height: '100%',
            }}>
              <PropertyListMap
                properties={properties}
                hoveredPropertyId={hoveredPropertyId}
                onMarkerClick={(propertyId) => fetchPropertyDetail(propertyId)}
              />
            </div>
          </div>
        )}

        {/* ===== 어드민 탭 콘텐츠 ===== */}
        {activeSection === 'admin' && user.role === 'admin' && (
          <AdminTab
            properties={properties}
            token={token}
            onClickProperty={(id: number) => fetchPropertyDetail(id)}
            onEditProperty={(id: number) => { setEditPropertyId(id); setShowRegistration(true); }}
            onUpdate={fetchProperties}
            formatPrice={formatPrice}
            convertImageUrl={convertImageUrl}
          />
        )}

        {/* ===== 어드민2 탭 콘텐츠 (회원 목록) ===== */}
        {activeSection === 'admin2' && user.role === 'admin' && (
          <MemberListTab token={token} />
        )}

        {activeSection === 'review' && user.role === 'admin' && !selectedProperty && (
          <div style={{ padding: '20px' }}>
            <h2 style={{ marginBottom: '20px' }}>심사 대기 중인 매물</h2>

            {reviewProperties.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '100px 20px' }}>
                <h3 style={{ color: '#999' }}>심사 대기 중인 매물이 없습니다</h3>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                {/* 리스트 헤더 */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '80px 1fr 200px 160px 100px',
                  padding: '12px 16px', backgroundColor: '#f8f8f8', borderBottom: '2px solid #e0e0e0',
                  fontSize: '13px', fontWeight: 'bold', color: '#666', gap: '12px', alignItems: 'center'
                }}>
                  <span>사진</span>
                  <span>매물 정보</span>
                  <span>주소</span>
                  <span style={{ textAlign: 'right' }}>임대료</span>
                  <span style={{ textAlign: 'center' }}>상태</span>
                </div>

                {/* 리스트 아이템 */}
                {reviewProperties.map(property => (
                  <div
                    key={property.id}
                    onClick={() => fetchPropertyDetail(property.id)}
                    style={{
                      display: 'grid', gridTemplateColumns: '80px 1fr 200px 160px 100px',
                      padding: '14px 16px', borderBottom: '1px solid #f0f0f0',
                      cursor: 'pointer', transition: 'background-color 0.15s',
                      alignItems: 'center', gap: '12px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FAFAFA'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {/* 썸네일 */}
                    <div style={{
                      width: '80px', height: '56px', borderRadius: '6px', overflow: 'hidden',
                      backgroundColor: '#f0f0f0', flexShrink: 0
                    }}>
                      {property.photos && property.photos.length > 0 ? (
                        <img
                          src={convertImageUrl(property.photos[0].photo_url)}
                          alt={property.descriptions?.room_name || '매물 사진'}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <div style={{
                          width: '100%', height: '100%', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          fontSize: '11px', color: '#bbb'
                        }}>없음</div>
                      )}
                    </div>

                    {/* 매물 정보 */}
                    <div style={{ minWidth: 0 }}>
                      <p style={{
                        margin: 0, fontSize: '15px', fontWeight: '600', color: '#222',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}>
                        {property.descriptions?.room_name || '제목 없음'}
                      </p>
                      <p style={{ margin: '3px 0 0 0', fontSize: '13px', color: '#888' }}>
                        {property.space?.building_type} · 방 {property.space?.room_count}개 · {property.space?.exclusive_area}평
                      </p>
                    </div>

                    {/* 주소 */}
                    <p style={{
                      margin: 0, fontSize: '13px', color: '#666',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                      {property.space?.road_address}
                    </p>

                    {/* 임대료 */}
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#222' }}>
                        {formatPrice(property.rent?.weekly_rent || 0)}원/주
                      </p>
                      <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#999' }}>
                        보증금 {formatPrice(property.rent?.deposit || 0)}원
                      </p>
                    </div>

                    {/* 상태 배지 */}
                    <div style={{ textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block', padding: '4px 12px',
                        backgroundColor: '#FF6B6B', color: '#fff',
                        borderRadius: '4px', fontSize: '12px', fontWeight: 'bold'
                      }}>
                        심사중
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 방심사 상세 화면 - PropertyDetail 재사용 */}
        {activeSection === 'review' && user.role === 'admin' && selectedProperty && (
          <PropertyDetail
            property={selectedProperty}
            onBack={() => setSelectedProperty(null)}
            onEdit={(id) => {
              setEditPropertyId(id);
              setShowRegistration(true);
            }}
            onRequestReview={(id) => requestReview(id)}
            reviewMode={true}
            onApprove={(id) => approveProperty(id)}
            onReject={(id) => rejectProperty(id)}
            onAiReview={(id) => aiReviewProperty(id)}
            aiReviewing={aiReviewing}
          />
        )}

        {/* ===== 검수 완료 탭 콘텐츠 ===== */}
        {activeSection === 'completed' && user.role === 'admin' && !selectedProperty && (
          <div>
            <h2 style={{ textAlign: 'center', marginBottom: '30px' }}>검수 완료 매물</h2>

            {completedProperties.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '100px 20px' }}>
                <h3 style={{ color: '#999' }}>검수 완료된 매물이 없습니다</h3>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                {/* 리스트 헤더 */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '80px 1fr 200px 160px 100px',
                  padding: '12px 16px', backgroundColor: '#f8f8f8', borderBottom: '2px solid #e0e0e0',
                  fontSize: '13px', fontWeight: 'bold', color: '#666', gap: '12px', alignItems: 'center'
                }}>
                  <span>사진</span>
                  <span>매물 정보</span>
                  <span>주소</span>
                  <span style={{ textAlign: 'right' }}>임대료</span>
                  <span style={{ textAlign: 'center' }}>상태</span>
                </div>

                {/* 리스트 아이템 */}
                {completedProperties.map(property => {
                  const statusConfig: Record<string, { label: string; color: string }> = {
                    '검수승인': { label: '승인', color: '#4CAF50' },
                    '검수반려': { label: '반려', color: '#F44336' },
                    '검수보완': { label: '보완요청', color: '#FF9800' },
                  };
                  const badge = statusConfig[property.status || ''] || { label: property.status || '', color: '#999' };

                  return (
                    <div
                      key={property.id}
                      onClick={() => fetchPropertyDetail(property.id)}
                      style={{
                        display: 'grid', gridTemplateColumns: '80px 1fr 200px 160px 100px',
                        padding: '14px 16px', borderBottom: '1px solid #f0f0f0',
                        cursor: 'pointer', transition: 'background-color 0.15s',
                        alignItems: 'center', gap: '12px'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FAFAFA'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {/* 썸네일 */}
                      <div style={{
                        width: '80px', height: '56px', borderRadius: '6px', overflow: 'hidden',
                        backgroundColor: '#f0f0f0', flexShrink: 0
                      }}>
                        {property.photos && property.photos.length > 0 ? (
                          <img
                            src={convertImageUrl(property.photos[0].photo_url)}
                            alt={property.descriptions?.room_name || '매물 사진'}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{
                            width: '100%', height: '100%', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            fontSize: '11px', color: '#bbb'
                          }}>없음</div>
                        )}
                      </div>

                      {/* 매물 정보 */}
                      <div style={{ minWidth: 0 }}>
                        <p style={{
                          margin: 0, fontSize: '15px', fontWeight: '600', color: '#222',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }}>
                          {property.descriptions?.room_name || '제목 없음'}
                        </p>
                        <p style={{ margin: '3px 0 0 0', fontSize: '13px', color: '#888' }}>
                          {property.space?.building_type} · 방 {property.space?.room_count}개 · {property.space?.exclusive_area}평
                        </p>
                      </div>

                      {/* 주소 */}
                      <p style={{
                        margin: 0, fontSize: '13px', color: '#666',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}>
                        {property.space?.road_address}
                      </p>

                      {/* 임대료 */}
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#222' }}>
                          {formatPrice(property.rent?.weekly_rent || 0)}원/주
                        </p>
                        <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#999' }}>
                          보증금 {formatPrice(property.rent?.deposit || 0)}원
                        </p>
                      </div>

                      {/* 상태 변경 */}
                      <div style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <select
                          value={property.status || ''}
                          onChange={(e) => {
                            if (window.confirm(`상태를 변경하시겠습니까?`)) {
                              changePropertyStatus(property.id, e.target.value);
                            }
                          }}
                          style={{
                            padding: '4px 8px', borderRadius: '4px', fontSize: '12px',
                            fontWeight: 'bold', color: '#fff', border: 'none', cursor: 'pointer',
                            backgroundColor: badge.color, appearance: 'auto'
                          }}
                        >
                          <option value="심사중">검수중</option>
                          <option value="검수승인">승인</option>
                          <option value="검수반려">반려</option>
                          <option value="검수보완">보완요청</option>
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* 검수 완료 상세 화면 */}
        {activeSection === 'completed' && user.role === 'admin' && selectedProperty && (
          <PropertyDetail
            property={selectedProperty}
            onBack={() => setSelectedProperty(null)}
            onEdit={(id) => {
              setEditPropertyId(id);
              setShowRegistration(true);
            }}
            onRequestReview={(id) => requestReview(id)}
          />
        )}

        {/* ===== 방 노출 탭 콘텐츠 (소형 카드 + 카카오맵) ===== */}
        {activeSection === 'exposure' && user.role === 'admin' && !selectedProperty && (
          <div style={{ display: 'flex', height: 'calc(100vh - 65px)' }}>
            {/* 왼쪽: 소형 카드 리스트 */}
            <div style={{
              width: '420px',
              minWidth: '420px',
              overflowY: 'auto',
              padding: '16px 12px',
              textAlign: 'left',
              borderRight: '1px solid #eee',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700' }}>노출 중인 매물 {exposedProperties.length}개</h2>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {exposedProperties.map(property => (
                  <ExposureCard
                    key={property.id}
                    property={property}
                    isHovered={hoveredPropertyId === property.id}
                    onMouseEnter={() => setHoveredPropertyId(property.id)}
                    onMouseLeave={() => setHoveredPropertyId(null)}
                    onClick={() => fetchPropertyDetail(property.id)}
                    formatPrice={formatPrice}
                    convertImageUrl={convertImageUrl}
                  />
                ))}
              </div>

              {exposedProperties.length === 0 && (
                <p style={{ textAlign: 'center', color: '#666', marginTop: '60px' }}>노출 중인 매물이 없습니다.</p>
              )}
            </div>

            {/* 오른쪽: 카카오맵 */}
            <div style={{ flex: 1, height: '100%' }}>
              <ExposureMap
                properties={exposedProperties}
                hoveredPropertyId={hoveredPropertyId}
                onMarkerClick={(propertyId) => fetchPropertyDetail(propertyId)}
              />
            </div>
          </div>
        )}

        {/* 방 노출 상세 화면 */}
        {activeSection === 'exposure' && user.role === 'admin' && selectedProperty && (
          <PropertyDetail
            property={selectedProperty}
            onBack={() => setSelectedProperty(null)}
            onEdit={(id) => {
              setEditPropertyId(id);
              setShowRegistration(true);
            }}
            onRequestReview={(id) => requestReview(id)}
          />
        )}

        {/* ===== 검수 기준 탭 콘텐츠 ===== */}
        {activeSection === 'criteria' && user.role === 'admin' && (
          <div style={{ padding: '20px', textAlign: 'left' }}>
            <h2 style={{ marginBottom: '8px', fontSize: '24px' }}>매물 검수 기준 PRD</h2>
            <p style={{ margin: '0 0 30px 0', fontSize: '14px', color: '#888' }}>
              매물 검수 규칙 + AI 판독용 기준 문서
            </p>

            {/* ===== 1. 매물 검수 프로세스 ===== */}
            <div style={{ marginBottom: '40px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', margin: '0 0 12px 0', borderBottom: '2px solid #333', paddingBottom: '8px' }}>
                1. 매물 검수 프로세스
              </h3>
              <p style={{ fontSize: '13px', color: '#888', margin: '0 0 16px 0' }}>
                * 검수 보완은 있는게 좋음(매물 품질 목적), 가능여부 추후 검토 필요
              </p>

              {/* 프로세스 흐름도 */}
              <div style={{ backgroundColor: '#f8f8f8', borderRadius: '10px', padding: '24px', marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '16px' }}>일반적 매물 등록 상황</h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* 검수 단계 */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '14px' }}>
                    <span style={{ padding: '6px 12px', backgroundColor: '#333', color: '#fff', borderRadius: '6px', fontWeight: 'bold' }}>매물 등록 검수</span>
                    <span style={{ color: '#999' }}>→</span>
                    <span style={{ padding: '6px 12px', backgroundColor: '#E3F2FD', borderRadius: '6px' }}>매물 검수 (시스템+AI)</span>
                    <span style={{ fontSize: '13px', color: '#666' }}>: 1. 시스템 규칙 / 2. AI Agent</span>
                  </div>

                  {/* 결과 1: 승인 */}
                  <div style={{ padding: '14px 16px', backgroundColor: '#E8F5E9', borderRadius: '8px', borderLeft: '4px solid #4CAF50' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '13px' }}>
                      <span style={{ fontWeight: 'bold', color: '#2E7D32' }}>1. 검수 완료 (승인)</span>
                      <span>→ 노출중 → 시스템 알림 메시지 → 호스트 수정 → 시스템 매물 검수</span>
                    </div>
                    <div style={{ marginTop: '6px', fontSize: '13px', color: '#555', paddingLeft: '16px' }}>
                      → 1. 통과 → 검수 완료 → <strong style={{ color: '#2E7D32' }}>매물 노출중</strong>
                    </div>
                    <div style={{ marginTop: '4px', fontSize: '13px', color: '#555', paddingLeft: '16px' }}>
                      → 2. 반려(2차) → <span style={{ color: '#E65100' }}>사람 개입 (+ 영업 친밀도 강화)</span> → 검수 완료 → <strong style={{ color: '#2E7D32' }}>매물 노출중</strong>
                    </div>
                  </div>

                  {/* 결과 2: 보완 */}
                  <div style={{ padding: '14px 16px', backgroundColor: '#FFF3E0', borderRadius: '8px', borderLeft: '4px solid #FF9800' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '13px' }}>
                      <span style={{ fontWeight: 'bold', color: '#E65100' }}>2. 검수 완료 (보완)</span>
                      <span>→ 노출중 → 시스템 알림 메시지 → 호스트 수정 → 시스템 매물 검수</span>
                    </div>
                    <div style={{ marginTop: '6px', fontSize: '13px', color: '#555', paddingLeft: '16px' }}>
                      → 1. 통과 → 검수 완료 → <strong style={{ color: '#2E7D32' }}>매물 노출중</strong>
                    </div>
                    <div style={{ marginTop: '4px', fontSize: '13px', color: '#555', paddingLeft: '16px' }}>
                      → 2. 반려(2차) → <span style={{ color: '#E65100' }}>사람 개입 (+ 영업 친밀도 강화)</span> → 검수 완료 → <strong style={{ color: '#2E7D32' }}>매물 노출중</strong>
                    </div>
                  </div>

                  {/* 결과 3: 반려 */}
                  <div style={{ padding: '14px 16px', backgroundColor: '#FFEBEE', borderRadius: '8px', borderLeft: '4px solid #F44336' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '13px' }}>
                      <span style={{ fontWeight: 'bold', color: '#C62828' }}>3. 반려</span>
                      <span>→ 노출 불가 → 불가 알림 메시지 → 호스트 수정 → 시스템 매물 검수</span>
                    </div>
                    <div style={{ marginTop: '6px', fontSize: '13px', color: '#555', paddingLeft: '16px' }}>
                      → 1. 통과 → 검수 완료 → <strong style={{ color: '#2E7D32' }}>매물 노출중</strong>
                    </div>
                    <div style={{ marginTop: '4px', fontSize: '13px', color: '#555', paddingLeft: '16px' }}>
                      → 2. 반려(2차) → <span style={{ color: '#E65100' }}>사람 개입 (+ 영업 친밀도 강화)</span> → 검수 완료 → <strong style={{ color: '#2E7D32' }}>매물 노출중</strong>
                    </div>
                  </div>
                </div>

                <p style={{ margin: '12px 0 0 0', fontSize: '12px', color: '#999', fontStyle: 'italic' }}>
                  ** 사람 개입 시점은 선택 (2차 or 3차 전화 실패시만)
                </p>
              </div>
            </div>

            {/* ===== 2. 매물 등록 & 검수 항목 A to Z ===== */}
            <div style={{ marginBottom: '40px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', margin: '0 0 12px 0', borderBottom: '2px solid #333', paddingBottom: '8px' }}>
                2. 매물 등록 & 검수 항목 A to Z
              </h3>

              {/* 목적 설명 */}
              <div style={{ backgroundColor: '#FFF8E1', borderRadius: '8px', padding: '16px', marginBottom: '20px', fontSize: '14px', lineHeight: '1.8', color: '#555' }}>
                <p style={{ margin: '0 0 6px 0', fontWeight: 'bold', color: '#333' }}>매물 검수 목적 (전체)</p>
                <p style={{ margin: 0 }}>
                  매물 등록 단계에서 (규칙 검수가 전제 되면), 매물 검수 과정은 최소화 되는것이 좋음.<br />
                  (매물은 검수 단계에 진입하기 전, 등록 단계에서 기본적인 검증이 완료되어야 함)<br />
                  매물 검수는 등록 단계에서 자동화로 처리하기 어려운 예외적인 경우에 한해 보완적으로 운영
                </p>
              </div>

              {/* 등록 항목 테이블 */}
              <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f0f0f0' }}>
                      <th style={{ padding: '10px 12px', border: '1px solid #ddd', textAlign: 'left', fontWeight: 'bold', whiteSpace: 'nowrap' }}>등록기준</th>
                      <th style={{ padding: '10px 12px', border: '1px solid #ddd', textAlign: 'left', fontWeight: 'bold' }}>항목</th>
                      <th style={{ padding: '10px 12px', border: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold', whiteSpace: 'nowrap' }}>등록(필수/조건)</th>
                      <th style={{ padding: '10px 12px', border: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold', whiteSpace: 'nowrap' }}>검수 기준(대상)</th>
                      <th style={{ padding: '10px 12px', border: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold', whiteSpace: 'nowrap' }}>추가 사유<br /><span style={{ fontSize: '11px', color: '#999' }}>*신고 제외</span></th>
                      <th style={{ padding: '10px 12px', border: '1px solid #ddd', textAlign: 'left', fontWeight: 'bold' }}>기준</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* 위치 및 구조 */}
                    <tr style={{ backgroundColor: '#FAFAFA' }}>
                      <td rowSpan={9} style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold', verticalAlign: 'top', backgroundColor: '#f8f8f8' }}>위치 및 구조</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>주소</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}></td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}></td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>주소 검색 API로 입력되는 주소, 수정불가로 조치</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>동</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>조건부 필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>대상</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}></td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>"동 정보 없음" 선택 시 예외</td>
                    </tr>
                    <tr style={{ backgroundColor: '#FAFAFA' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>호</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>대상</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}></td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>호수/호실 입력</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>전체 층</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>선택 필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>검수대상 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}></td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>전체 층수 선택 <span style={{ color: '#1976D2', fontSize: '12px' }}>**건축물대장 API</span></td>
                    </tr>
                    <tr style={{ backgroundColor: '#FAFAFA' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>해당 층</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>선택 필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>대상</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}></td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>반지하/1층~옥탑</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>건물 유형</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>검수대상 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#F44336' }}>신고 대상</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>11개 유형 중 선택 <span style={{ color: '#1976D2', fontSize: '12px' }}>**건축물대장 API</span></td>
                    </tr>
                    <tr style={{ backgroundColor: '#FAFAFA' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>방 수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>검수대상 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#F44336' }}>신고 대상</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>최소 1개</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>엘리베이터</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>검수대상 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#F44336' }}>신고 대상</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>있음/없음</td>
                    </tr>
                    <tr style={{ backgroundColor: '#FAFAFA' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>주차 가능 여부</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>검수대상 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#F44336' }}>신고 대상</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>불가능/가능/제한적 가능</td>
                    </tr>

                    {/* 옵션 */}
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f8f8f8' }}>옵션</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>반려동물 동반 여부</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>검수대상 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#F44336' }}>신고 대상</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>허용/불가</td>
                    </tr>

                    {/* 상세 정보 */}
                    <tr style={{ backgroundColor: '#FAFAFA' }}>
                      <td rowSpan={4} style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold', verticalAlign: 'top', backgroundColor: '#f8f8f8' }}>상세 정보</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>매물 한 줄 소개(매물명)</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>대상</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>금칙어</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>5~20자</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>매물 사진</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>대상</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>혼돌림/중복</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>최소 5장~최대 30장</td>
                    </tr>
                    <tr style={{ backgroundColor: '#FAFAFA' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>대표 사진</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>자동</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>대상</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>대표 x / 혼돌림/중복</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>첫 번째 사진</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>매물 소개</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>대상</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>금칙어 ~</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>최소 30자~최대 1000자</td>
                    </tr>

                    {/* 요금 설정 */}
                    <tr style={{ backgroundColor: '#FAFAFA' }}>
                      <td rowSpan={6} style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold', verticalAlign: 'top', backgroundColor: '#f8f8f8' }}>요금 설정</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>주당 요금</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>검수대상 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>등록 기준</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>(주기준) 최소 1만원 ~ 최대 1,000만원</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>관리비</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>검수대상 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>등록 기준</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>(주기준) 최소 1만원 ~ 최대 100만원</td>
                    </tr>
                    <tr style={{ backgroundColor: '#FAFAFA' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>청소비</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>검수대상 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>등록 기준</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>최대 30만원</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>보증금</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>검수대상 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>등록 기준</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>고정값</td>
                    </tr>
                    <tr style={{ backgroundColor: '#FAFAFA' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>위약금 규정</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>검수대상 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>정책</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>유연/표준/엄격</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>위약금 주의사항 표시</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>검수대상 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>정책</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>체크 여부</td>
                    </tr>

                    {/* 예약 설정 */}
                    <tr style={{ backgroundColor: '#FAFAFA' }}>
                      <td rowSpan={2} style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold', verticalAlign: 'top', backgroundColor: '#f8f8f8' }}>예약 설정</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>최소 계약 기간</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>검수대상 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>정책 / 등록 선택</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>1주 ~ 12주 / 1개월~3개월</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>일정 관리</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>등록 필수 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>검수대상 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>등록 선택</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>정책상 선택 가능</td>
                    </tr>

                    {/* 입실·퇴실 규칙 */}
                    <tr style={{ backgroundColor: '#FAFAFA' }}>
                      <td rowSpan={4} style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold', verticalAlign: 'top', backgroundColor: '#f8f8f8' }}>입실·퇴실 규칙</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>입실 시간</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>검수대상 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>등록 선택</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>기본 15:00</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>입실 방식</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>검수대상 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}></td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>대면/비대면</td>
                    </tr>
                    <tr style={{ backgroundColor: '#FAFAFA' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>퇴실 시간</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>검수대상 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}></td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>기본 11:00</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>퇴실 방식</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>필수</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', color: '#999' }}>검수대상 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}></td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>대면/비대면</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* ===== 3. 매물 검수 기준 ===== */}
            <div style={{ marginBottom: '40px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', margin: '0 0 12px 0', borderBottom: '2px solid #333', paddingBottom: '8px' }}>
                3. 매물 검수 기준
              </h3>

              {/* 검수 요약 카드 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div style={{ backgroundColor: '#f8f8f8', borderRadius: '10px', padding: '20px', textAlign: 'center' }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666' }}>검수항목</p>
                  <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold', color: '#333' }}>13</p>
                </div>
                <div style={{ backgroundColor: '#FFEBEE', borderRadius: '10px', padding: '20px', textAlign: 'center' }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#C62828' }}>검수반려</p>
                  <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold', color: '#C62828' }}>10</p>
                </div>
                <div style={{ backgroundColor: '#FFF3E0', borderRadius: '10px', padding: '20px', textAlign: 'center' }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#E65100' }}>검수보완</p>
                  <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold', color: '#E65100' }}>3</p>
                </div>
              </div>

              {/* 반려/보완 사유 분류 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div style={{ backgroundColor: '#FFEBEE', borderRadius: '10px', padding: '16px' }}>
                  <p style={{ margin: '0 0 10px 0', fontSize: '15px', fontWeight: 'bold', color: '#C62828' }}>검수반려사유 (10건)</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: '#555' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>사진</span><strong>8건</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>주소</span><strong>1건</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>호스트</span><strong>1건</strong></div>
                  </div>
                </div>
                <div style={{ backgroundColor: '#FFF3E0', borderRadius: '10px', padding: '16px' }}>
                  <p style={{ margin: '0 0 10px 0', fontSize: '15px', fontWeight: 'bold', color: '#E65100' }}>검수보완사유 (3건)</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: '#555' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>사진</span><strong>3건</strong></div>
                  </div>
                </div>
              </div>

              {/* 상세 검수 기준 테이블 */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#333', color: '#fff' }}>
                      <th style={{ padding: '10px 12px', border: '1px solid #555', textAlign: 'left', whiteSpace: 'nowrap' }}>검수항목_1</th>
                      <th style={{ padding: '10px 12px', border: '1px solid #555', textAlign: 'left', whiteSpace: 'nowrap' }}>검수항목_2</th>
                      <th style={{ padding: '10px 12px', border: '1px solid #555', textAlign: 'center', whiteSpace: 'nowrap' }}>상태값</th>
                      <th style={{ padding: '10px 12px', border: '1px solid #555', textAlign: 'left' }}>반려사유</th>
                      <th style={{ padding: '10px 12px', border: '1px solid #555', textAlign: 'left' }}>매물 검수 기준</th>
                      <th style={{ padding: '10px 12px', border: '1px solid #555', textAlign: 'center', whiteSpace: 'nowrap' }}>AI 검수</th>
                      <th style={{ padding: '10px 12px', border: '1px solid #555', textAlign: 'left' }}>비고</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* 호스트 - 특별관리 */}
                    <tr style={{ backgroundColor: '#FFF9C4' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold' }}>호스트</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>특별관리</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', backgroundColor: '#F44336', color: '#fff', borderRadius: '4px', fontSize: '12px' }}>검수반려</span>
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>특별관리 대상 호스트</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>매물 신고 3회 이상 호스트 매물</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>시스템 가능</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', color: '#888' }}>신고 누적 기준</td>
                    </tr>

                    {/* 주소 - 중복등록 */}
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold' }}>주소</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>중복등록</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', backgroundColor: '#F44336', color: '#fff', borderRadius: '4px', fontSize: '12px' }}>검수반려</span>
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>동일 주소(동/호수) 중복 등록</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>동일주소 매물 중복 존재 여부</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>O</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', color: '#888' }}>중복 태그 / 등록 불가 기준</td>
                    </tr>

                    {/* 사진 - 중복사진 */}
                    <tr style={{ backgroundColor: '#FAFAFA' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold' }}>사진</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>중복사진</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', backgroundColor: '#F44336', color: '#fff', borderRadius: '4px', fontSize: '12px' }}>검수반려</span>
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>중복사진이 많은 경우</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>등록 사진 수 대비 중복 사진 50% 이상인 경우</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>O</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', color: '#888' }}>각도만 살짝 다른 동일컷 포함</td>
                    </tr>

                    {/* 사진 - 다른사진 */}
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold' }}>사진</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>다른사진</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', backgroundColor: '#F44336', color: '#fff', borderRadius: '4px', fontSize: '12px' }}>검수반려</span>
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>매물 외 사진 등록</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>공간과 관련없는 사진이 50% 이상인 경우</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>O</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', color: '#888' }}>공간 파악 불가</td>
                    </tr>

                    {/* 사진 - 내부사진없음 */}
                    <tr style={{ backgroundColor: '#FAFAFA' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold' }}>사진</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>내부사진없음</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', backgroundColor: '#F44336', color: '#fff', borderRadius: '4px', fontSize: '12px' }}>검수반려</span>
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>실내 사진이 없는 경우</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>실내 사진이 없고, 건물외관만 인테리어 등이 있는 경우</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>O</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', color: '#888' }}>공간 파악 불가</td>
                    </tr>

                    {/* 사진 - 외부연락유도 (전화번호) */}
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold' }}>사진</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>외부연락유도</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', backgroundColor: '#F44336', color: '#fff', borderRadius: '4px', fontSize: '12px' }}>검수반려</span>
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>사진 내 금칙어 - 전화번호, 카톡 등 연락처</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>전화번호, 카톡등 연락처가 사진에 포함 되어 있는 경우</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>O</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}></td>
                    </tr>

                    {/* 사진 - 외부연락유도 (외부 링크) */}
                    <tr style={{ backgroundColor: '#FAFAFA' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold' }}>사진</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>외부연락유도</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', backgroundColor: '#F44336', color: '#fff', borderRadius: '4px', fontSize: '12px' }}>검수반려</span>
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>사진 내 대처이 - 외부 링크 등</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>URL, 도메인, 오픈채팅, 인스타, 블로그 링크가 사진에 포함되어 있는 경우</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>O</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}></td>
                    </tr>

                    {/* 사진 - 외부연락유도 (QR) */}
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold' }}>사진</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>외부연락유도</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', backgroundColor: '#F44336', color: '#fff', borderRadius: '4px', fontSize: '12px' }}>검수반려</span>
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>QR코드 등 포함</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>QR 코드가 사진에 포함되어 있는 경우</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>O</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}></td>
                    </tr>

                    {/* 사진 - 직거래유도 */}
                    <tr style={{ backgroundColor: '#FAFAFA' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold' }}>사진</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>직거래유도</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', backgroundColor: '#F44336', color: '#fff', borderRadius: '4px', fontSize: '12px' }}>검수반려</span>
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>사진 내 금칙어 - 계좌번호 등 포함</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>계좌번호가 사진에 포함되어 있는 경우</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>O</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}></td>
                    </tr>

                    {/* 사진 - 숙박업오인 */}
                    <tr>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold' }}>사진</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>숙박업오인</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', backgroundColor: '#F44336', color: '#fff', borderRadius: '4px', fontSize: '12px' }}>검수반려</span>
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>사진 내 금칙어 - 숙박업 오인</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>호텔, 모텔 등 숙박업 오인 금칙어 내용이 포함되어 있는 경우</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>O</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}></td>
                    </tr>

                    {/* 사진 - 검수보완: 구성 편중 */}
                    <tr style={{ backgroundColor: '#FFF8E1' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold' }}>사진</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>사진</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', backgroundColor: '#FF9800', color: '#fff', borderRadius: '4px', fontSize: '12px' }}>검수보완</span>
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>특정 공간만 반복(구성 편중)</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>특정 공간 반복으로 전체 공간 파악 가능 여부</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>O</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', color: '#888' }}>침실만 여러 장 등</td>
                    </tr>

                    {/* 사진 - 검수보완: 어둠 */}
                    <tr style={{ backgroundColor: '#FFF8E1' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold' }}>사진</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>사진</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', backgroundColor: '#FF9800', color: '#fff', borderRadius: '4px', fontSize: '12px' }}>검수보완</span>
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>사진 품질 저하(어둠)</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>어두움/역광으로 상태 확인 가능한 수준이 아님</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>O</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', color: '#888' }}>너무 어두워 내부 안 보임</td>
                    </tr>

                    {/* 사진 - 검수보완: 흔들림/초점/저해상도 */}
                    <tr style={{ backgroundColor: '#FFF8E1' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 'bold' }}>사진</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>사진</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', backgroundColor: '#FF9800', color: '#fff', borderRadius: '4px', fontSize: '12px' }}>검수보완</span>
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>사진 품질 저하(흔들림/초점/저해상도)</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>흔들림/초점이나 해상도/픽셀 깨짐으로 확인 불가 여부</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center' }}>O</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', color: '#888' }}>모션블러, 초점 흐림</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        )}


        {/* 매물 상세 화면 (인라인) */}
        {(activeSection === 'rooms' || activeSection === 'registration') && selectedProperty && (
          <PropertyDetail
            property={selectedProperty}
            onBack={() => setSelectedProperty(null)}
            onEdit={(id) => {
              setEditPropertyId(id);
              setShowRegistration(true);
            }}
            onRequestReview={(id) => requestReview(id)}
          />
        )}
      </div>

      {/* 어드민 매물 상세 모달 */}
      {activeSection === 'admin' && user.role === 'admin' && selectedProperty && (
        <div
          onClick={() => setSelectedProperty(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '62vw', maxWidth: '845px', height: '95vh',
              backgroundColor: '#fff', borderRadius: '12px',
              overflow: 'hidden', display: 'flex', flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            {/* 모달 헤더 */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 24px', borderBottom: '1px solid #eee', flexShrink: 0,
            }}>
              <span style={{ fontSize: '16px', fontWeight: '700' }}>매물 상세</span>
              <span
                onClick={() => setSelectedProperty(null)}
                style={{ fontSize: '24px', cursor: 'pointer', color: '#999', lineHeight: 1 }}
              >
                &times;
              </span>
            </div>
            {/* 모달 본문 — "목록으로 돌아가기" 숨김 */}
            <div className="admin-detail-modal" style={{ flex: 1, overflowY: 'auto', padding: '0 20px 20px' }}>
              <style>{`.admin-detail-modal > div > button:first-child { display: none !important; }`}</style>
              <PropertyDetail
                property={selectedProperty}
                onBack={() => setSelectedProperty(null)}
                onEdit={(id) => {
                  setEditPropertyId(id);
                  setShowRegistration(true);
                }}
                onRequestReview={(id) => requestReview(id)}
                adminMode={true}
              />
            </div>
          </div>
        </div>
      )}

      {/* 모달 - 네비게이션 바 위에 표시 */}
      {showRegistration && (
        <PropertyRegistration
          onClose={() => {
            setShowRegistration(false);
            setEditPropertyId(undefined);
          }}
          onSuccess={handleRegistrationSuccess}
          propertyId={editPropertyId}
          token={token}
        />
      )}

      {/* AI 심사 결과 모달 */}
      {showAiReviewModal && aiReviewResult && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            {/* 모달 헤더 */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e0e0e0',
              backgroundColor: aiReviewResult.approved ? '#E8F5E9' : '#FFEBEE'
            }}>
              <h2 style={{ margin: '0 0 10px 0', fontSize: '24px' }}>
                🤖 AI 심사 결과
              </h2>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '20px'
              }}>
                <div style={{
                  fontSize: '32px',
                  fontWeight: 'bold',
                  color: aiReviewResult.approved ? '#4CAF50' : '#F44336'
                }}>
                  {aiReviewResult.score}점
                </div>
                <div style={{
                  padding: '8px 16px',
                  backgroundColor: aiReviewResult.approved ? '#4CAF50' : '#F44336',
                  color: 'white',
                  borderRadius: '6px',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  {aiReviewResult.approved ? '✓ 승인 권장' : '✗ 거절 권장'}
                </div>
              </div>
            </div>

            {/* 모달 내용 - 스크롤 가능 */}
            <div style={{
              padding: '20px',
              overflowY: 'auto',
              flex: 1
            }}>
              {/* 사유 */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>사유</h3>
                <p style={{
                  margin: 0,
                  padding: '15px',
                  backgroundColor: '#f8f8f8',
                  borderRadius: '8px',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-line'
                }}>
                  {aiReviewResult.reason}
                </p>
              </div>

              {/* 체크리스트 */}
              {aiReviewResult.checklist && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>체크리스트</h3>
                  {Object.entries(aiReviewResult.checklist).map(([category, items]) => (
                    <div key={category} style={{ marginBottom: '15px' }}>
                      <h4 style={{
                        margin: '0 0 8px 0',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: '#666'
                      }}>
                        [{category}]
                      </h4>
                      <div style={{
                        backgroundColor: '#f8f8f8',
                        borderRadius: '8px',
                        padding: '10px'
                      }}>
                        {(items as string[]).map((item, index) => (
                          <div key={index} style={{
                            padding: '5px 0',
                            fontSize: '14px',
                            color: '#333'
                          }}>
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 누락된 항목 */}
              {aiReviewResult.missing_items && aiReviewResult.missing_items.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{
                    margin: '0 0 10px 0',
                    fontSize: '16px',
                    color: '#F44336'
                  }}>
                    누락된 항목 ({aiReviewResult.missing_items.length}개)
                  </h3>
                  <div style={{
                    backgroundColor: '#FFEBEE',
                    borderRadius: '8px',
                    padding: '15px'
                  }}>
                    {aiReviewResult.missing_items.map((item: string, index: number) => (
                      <div key={index} style={{
                        padding: '5px 0',
                        fontSize: '14px',
                        color: '#D32F2F'
                      }}>
                        • {item}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 모달 푸터 */}
            <div style={{
              padding: '20px',
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              gap: '10px'
            }}>
              <button
                onClick={() => {
                  setShowAiReviewModal(false);
                  setAiReviewResult(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#f0f0f0',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
              >
                취소
              </button>
              <button
                onClick={handleAiReviewConfirm}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: aiReviewResult.approved ? '#4CAF50' : '#F44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = aiReviewResult.approved ? '#45A049' : '#D32F2F';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = aiReviewResult.approved ? '#4CAF50' : '#F44336';
                }}
              >
                확인 ({aiReviewResult.approved ? '승인' : '거절'})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
